// ./src/state/chat.ts
import { create } from 'zustand';
import {
  isChatLikePersisted,
  rememberChatLike,
  syncChatLikePersistence,
} from '@core/gameengine/chat/chatMessageLikes';
import { useAuth } from '@core/gameengine/state/auth';

// TODO: revamp chat state
interface ServerMessage {
  user: {
    nickName?: string;
    nickname?: string;
    playerId?: string;
    id?: string;
    roles?: string[];
  };
  type: 'CHAT' | string;
  content: {
    text: string;
    likeCount?: number;
    likes?: number;
    likedByMe?: boolean;
    liked?: boolean;
    clientSeq?: string;
    clientId?: string;
  };
  meta: {
    timestamp: number;
    clientSeq?: string;
    clientId?: string;
    likeCount?: number;
    likes?: number;
    likedByMe?: boolean;
    liked?: boolean;
  };
}

// Updated interface for a UI-ready chat message
export interface ChatMessage {
  id: string; // A unique ID for React keys
  clientSeq: string; // The original ID from the server
  user: string;
  text: string;
  /** Epoch ms — formatted for display via formatChatMessageTime(). */
  timestampMs: number;
  likeCount: number;
  likedByMe: boolean;
}

interface ChatState {
  messages: ChatMessage[];
  error: string | null;
  /** Whether the chat panel is currently open. */
  isChatOpen: boolean;
  /** True when new messages arrived while the chat panel was closed. */
  hasUnread: boolean;
  /** Latest message id the user has already seen (panel open or initial hydrate). */
  lastReadMessageId: string | null;
  updateMessages: (newMessages: ServerMessage[]) => void;
  addMessage: (
    newMessage: ServerMessage,
    options?: { isOwn?: boolean }
  ) => void;
  updateMessageLikes: (
    clientSeq: string,
    likeCount: number,
    likedByMe?: boolean
  ) => void;
  setError: (errorMessage: string) => void;
  setChatOpen: (isOpen: boolean) => void;
  setHasUnread: (hasUnread: boolean) => void;
}

const readLikeCount = (msg: ServerMessage): number => {
  const meta = msg.meta ?? ({} as ServerMessage['meta']);
  const content = msg.content ?? ({} as ServerMessage['content']);
  const value =
    meta.likeCount ?? meta.likes ?? content.likeCount ?? content.likes ?? 0;
  return typeof value === 'number' && value > 0 ? value : 0;
};

const readLikedByMe = (msg: ServerMessage): boolean =>
  Boolean(
    msg.meta?.likedByMe ??
      msg.meta?.liked ??
      msg.content?.likedByMe ??
      msg.content?.liked ??
      (msg as { likedByMe?: boolean }).likedByMe ??
      (msg as { liked?: boolean }).liked
  );

const resolveLikedByMe = (
  msg: ServerMessage,
  clientSeq: string,
  playerId: string | null | undefined
): boolean => {
  const fromServer = readLikedByMe(msg);
  if (fromServer) {
    rememberChatLike(playerId, clientSeq);
    return true;
  }
  return isChatLikePersisted(playerId, clientSeq);
};

const formatMessage = (msg: ServerMessage): ChatMessage => {
  const meta = msg.meta ?? ({} as ServerMessage['meta']);
  const content = msg.content ?? ({} as ServerMessage['content']);
  const clientSeq =
    meta.clientSeq ??
    meta.clientId ??
    content.clientSeq ??
    content.clientId ??
    '';

  const messageUser =
    msg.user?.nickName ??
    msg.user?.nickname ??
    msg.user?.playerId ??
    msg.user?.id ??
    '';

  return {
    id: `${clientSeq}-${meta.timestamp}`,
    clientSeq,
    user: messageUser,
    text: content.text,
    timestampMs: meta.timestamp,
    likeCount: readLikeCount(msg),
    likedByMe: resolveLikedByMe(
      msg,
      clientSeq,
      useAuth.getState().user?.playerId
    ),
  };
};

const latestMessageId = (messages: ChatMessage[]): string | null =>
  messages.length > 0 ? messages[messages.length - 1].id : null;

/** Unread messages since the panel was last open (0 when chat is open or nothing unread). */
export function getUnreadChatCount(
  state: Pick<
    ChatState,
    'messages' | 'lastReadMessageId' | 'isChatOpen' | 'hasUnread'
  >
): number {
  if (state.isChatOpen || !state.hasUnread) return 0;
  if (state.messages.length === 0) return 0;

  const { lastReadMessageId, messages } = state;
  if (lastReadMessageId == null) return 1;

  const lastReadIndex = messages.findIndex(m => m.id === lastReadMessageId);
  const count =
    lastReadIndex === -1
      ? messages.length
      : Math.max(0, messages.length - lastReadIndex - 1);

  return count > 0 ? count : 1;
}

export const useChat = create<ChatState>((set, get) => ({
  messages: [],
  error: null,
  isChatOpen: false,
  hasUnread: false,
  lastReadMessageId: null,

  updateMessages: newMessages => {
    const messageMap = new Map<string, ChatMessage>();
    newMessages.forEach(msg => {
      const formatted = formatMessage(msg);
      messageMap.set(formatted.id, formatted);
    });
    const messages = Array.from(messageMap.values());
    const nextLastId = latestMessageId(messages);
    const { isChatOpen, lastReadMessageId, hasUnread } = get();

    // First hydrate while closed: treat existing history as already read,
    // but keep any unread already set by a live message that arrived first.
    if (!isChatOpen && lastReadMessageId == null) {
      set({
        messages,
        lastReadMessageId: nextLastId,
        hasUnread,
      });
      return;
    }

    if (isChatOpen) {
      set({
        messages,
        lastReadMessageId: nextLastId,
        hasUnread: false,
      });
      return;
    }

    const hasNew =
      nextLastId != null &&
      nextLastId !== lastReadMessageId &&
      messages.length > 0;

    set({
      messages,
      hasUnread: hasNew ? true : hasUnread,
    });
  },

  addMessage: (newMessage, options) => {
    const formatted = formatMessage(newMessage);
    set(state => {
      const existingIndex = state.messages.findIndex(
        message => message.clientSeq === formatted.clientSeq
      );
      if (existingIndex >= 0) {
        const updated = [...state.messages];
        updated[existingIndex] = {
          ...updated[existingIndex],
          ...formatted,
          id: updated[existingIndex].id,
        };
        return { messages: updated };
      }
      if (state.messages.some(m => m.id === formatted.id)) {
        return state;
      }

      const messages = [...state.messages, formatted].slice(-150);
      if (state.isChatOpen) {
        return {
          messages,
          lastReadMessageId: formatted.id,
          hasUnread: false,
        };
      }

      if (options?.isOwn) {
        return {
          messages,
          lastReadMessageId: formatted.id,
        };
      }

      return {
        messages,
        hasUnread: true,
      };
    });
  },

  updateMessageLikes: (clientSeq, likeCount, likedByMe) => {
    const playerId = useAuth.getState().user?.playerId;
    if (typeof likedByMe === 'boolean') {
      syncChatLikePersistence(playerId, clientSeq, likedByMe);
    }

    set(state => ({
      messages: state.messages.map(message =>
        message.clientSeq === clientSeq
          ? {
              ...message,
              likeCount: Math.max(0, likeCount),
              likedByMe:
                typeof likedByMe === 'boolean' ? likedByMe : message.likedByMe,
            }
          : message
      ),
    }));
  },

  setError: errorMessage => {
    set({ error: errorMessage });
    setTimeout(() => set({ error: null }), 4000);
  },

  setChatOpen: isOpen => {
    if (isOpen) {
      const { messages } = get();
      set({
        isChatOpen: true,
        hasUnread: false,
        lastReadMessageId: latestMessageId(messages),
      });
      return;
    }
    set({ isChatOpen: false });
  },

  setHasUnread: hasUnread => set({ hasUnread }),
}));
